import type {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios'
import {DEFAULT_CACHE_DURATION} from '@/utils/constants/globalConstants'

const responseCache = new Map<string, {data: any; timestamp: number; status?: number; statusText?: string}>()
const pendingRequests = new Map<string, Promise<any>>()

const CACHE_HEADERS = {
  ENABLE_CACHE: 'x-enable-cache',
  NO_CACHE: 'x-no-cache',
}

export function clearCacheForEndpoint(endpoint: string) {
  for (const key of responseCache.keys()) {
    if (key.includes(endpoint)) responseCache.delete(key)
  }
}

export function clearAllCache() {
  responseCache.clear()
  pendingRequests.clear()
}

function getCacheKey(config: AxiosRequestConfig): string {
  const {method, url, params, data} = config
  const isPost = method?.toLowerCase() === 'post'
  const body = isPost && data ? `:${JSON.stringify(data)}` : ''
  return `${method || 'get'}:${url}:${JSON.stringify(params || {})}${body}`
}

function shouldCacheRequest(config: AxiosRequestConfig): boolean {
  if (!config.method) return false
  const method = config.method.toLowerCase()
  if (!['get', 'post'].includes(method)) return false
  const headers = config.headers || {}
  if (headers[CACHE_HEADERS.NO_CACHE] === 'true') return false
  return method === 'get' || headers[CACHE_HEADERS.ENABLE_CACHE] === 'true'
}

function createPendingAdapter(key: string, config: AxiosRequestConfig) {
  return () => {
    const pending = pendingRequests.get(key)
    if (!pending) {
      // Pending request already resolved and was cleaned up before this adapter ran.
      // Check if the result was cached; otherwise let Axios perform a fresh request.
      const cached = responseCache.get(key)
      if (cached && !(cached.data instanceof Error) && !(cached.data && cached.data.isAxiosError)) {
        return Promise.resolve({
          data: cached.data,
          status: cached.status || 200,
          statusText: cached.statusText || 'OK (deduped-cache)',
          headers: {},
          config,
          request: {},
        } as AxiosResponse)
      }
      // No cache available — reject so Axios can retry normally
      return Promise.reject(new Error('Pending request resolved before adapter executed'))
    }
    return pending.then(
      result =>
        ({
          data: result.data,
          status: result.status || 200,
          statusText: result.statusText || 'OK (deduped)',
          headers: {},
          config,
          request: {},
        }) as AxiosResponse,
    )
  }
}

function createCachedAdapter(
  cachedResponse: {data: any; status?: number; statusText?: string},
  config: AxiosRequestConfig,
) {
  return () =>
    Promise.resolve({
      data: cachedResponse.data,
      status: cachedResponse.status || 200,
      statusText: cachedResponse.statusText || 'OK (cache)',
      headers: {},
      config,
      request: {},
    } as AxiosResponse)
}

function markPending(key: string, config: any) {
  let resolve!: (data: any) => void
  let reject!: (err: any) => void

  const promise = new Promise((res, rej) => {
    resolve = res
    reject = rej
  })

  config.__resolvePending = resolve
  config.__rejectPending = reject
  pendingRequests.set(key, promise)
}

export function attachCacheInterceptor(axios: AxiosInstance, ttl = DEFAULT_CACHE_DURATION): AxiosInstance {
  axios.interceptors.request.use(config => {
    const shouldCache = shouldCacheRequest(config)
    const noCache = config.headers?.[CACHE_HEADERS.NO_CACHE] === 'true'

    ;(config as any).__shouldCache = shouldCache
    ;(config as any).__noCache = noCache

    delete config.headers?.[CACHE_HEADERS.NO_CACHE]
    delete config.headers?.[CACHE_HEADERS.ENABLE_CACHE]

    if (!shouldCache) return config

    const key = getCacheKey(config)

    if (pendingRequests.has(key)) {
      config.adapter = createPendingAdapter(key, config)
      return config
    }

    if (!noCache) {
      const cached = responseCache.get(key)
      if (cached && Date.now() - cached.timestamp < ttl) {
        // Check if cached data is an error
        if (cached.data instanceof Error || (cached.data && cached.data.isAxiosError)) {
          // Return cached error by rejecting the promise
          config.adapter = () => Promise.reject(cached.data)
        } else {
          // Return cached successful response
          config.adapter = createCachedAdapter(cached, config)
        }
        return config
      }
    }

    markPending(key, config)
    return config
  })

  axios.interceptors.response.use(
    response => {
      const {config} = response
      const key = getCacheKey(config)

      if ((config as any).__shouldCache && !(config as any).__noCache) {
        responseCache.set(key, {
          data: response.data,
          timestamp: Date.now(),
          status: response.status,
          statusText: response.statusText,
        })
      }

      ;(config as any).__resolvePending?.({
        data: response.data,
        status: response.status,
        statusText: response.statusText,
      })
      pendingRequests.delete(key)

      return response
    },
    error => {
      const config = error.config
      if (config) {
        const key = getCacheKey(config)

        // Cache failed responses just like successful ones
        if ((config as any).__shouldCache && !(config as any).__noCache) {
          responseCache.set(key, {data: error, timestamp: Date.now()})
        }

        ;(config as any).__rejectPending?.(error)
        pendingRequests.delete(key)
      }
      return Promise.reject(error)
    },
  )

  return axios
}
